import { computed } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useFilterSettingsStore, useListStore, usePopupStore, useViewSettingsStore } from '..';
import ListType from '../interfaces/ListType';
import { areCompanyFiltersDifferent, areDealFiltersDifferent, areLeadFiltersDifferent, areLeaseCompFiltersDifferent, areSaleCompFiltersDifferent, areTenantInMarketFiltersDifferent } from '../utils/filterSettingsUtils';
import CompanyIcon from './icons/CompanyIcon';
import DealIcon from './icons/DealIcon';
import FavoriteIcon from './icons/FavoriteIcon';
import LeadIcon from './icons/LeadIcon';
import LeaseCompIcon from './icons/LeaseCompIcon';
import NotificationIcon from './icons/NotificationIcon';
import RecityIcon from './icons/RecityIcon';
import SaleCompIcon from './icons/SaleCompIcon';
import TenantInMarketIcon from './icons/TenantInMarketIcon';
import styles from './Navbar.module.css';

const color = "#D2D2D2";
const withFiltersAppliedColor = "#FBBC04";

function Navbar() {
    const filterSettingsStore = useFilterSettingsStore();
    const popupStore = usePopupStore();
    const listStore = useListStore();
    const areDealFiltersApplied = useMemo(() => computed(() => areDealFiltersDifferent(filterSettingsStore.filters.dealSettings, filterSettingsStore.defaultFilters.dealSettings)), []).get();
    const areLeadFiltersApplied = useMemo(() => computed(() => areLeadFiltersDifferent(filterSettingsStore.filters.leadSettings, filterSettingsStore.defaultFilters.leadSettings)), []).get();
    const areLeaseCompFiltersApplied = useMemo(() => computed(() => areLeaseCompFiltersDifferent(filterSettingsStore.filters.leaseCompSettings, filterSettingsStore.defaultFilters.leaseCompSettings)), []).get();
    const areSaleCompFiltersApplied = useMemo(() => computed(() => areSaleCompFiltersDifferent(filterSettingsStore.filters.saleCompSettings, filterSettingsStore.defaultFilters.saleCompSettings)), []).get();
    const areCompanyFiltersApplied = useMemo(() => computed(() => areCompanyFiltersDifferent(filterSettingsStore.filters.companySettings, filterSettingsStore.defaultFilters.companySettings)), []).get();
    const areTenantInMarketFiltersApplied = useMemo(() => computed(() => areTenantInMarketFiltersDifferent(filterSettingsStore.filters.tenantInMarketSettings, filterSettingsStore.defaultFilters.tenantInMarketSettings)), []).get();

    return (
        <nav className={styles.container}>
            <Link to="/">
                <div className={styles.link} onClick={() => {
                    listStore.close();
                    popupStore.close();
                }}>
                    <RecityIcon />
                </div>
            </Link>
            {[
                {
                    title: "Deals", path: "/deals", type: ListType.Deals,
                    icon: <DealIcon color={areDealFiltersApplied ? withFiltersAppliedColor : color} />,
                },
                {
                    title: "Leads", path: "/leads", type: ListType.Leads,
                    icon: <LeadIcon color={areLeadFiltersApplied ? withFiltersAppliedColor : color} />,
                    hideInMobile: true,
                },
                {
                    title: "Lease Comps", path: "/leaseComps", type: ListType.LeaseComps,
                    icon: <LeaseCompIcon color={areLeaseCompFiltersApplied ? withFiltersAppliedColor : color} />,
                },
                {
                    title: "Sale Comps", path: "/saleComps", type: ListType.SaleComps,
                    icon: <SaleCompIcon color={areSaleCompFiltersApplied ? withFiltersAppliedColor : color} />,
                },
                {
                    title: "Companies", path: "/companies", type: ListType.Companies,
                    icon: <CompanyIcon color={areCompanyFiltersApplied ? "#FBBC04" : "#D2D2D2"}  />,
                    hideInMobile: true,
                },
                {
                    title: "Tenants in Market", path: "/tenantsInMarket", type: ListType.TenantsInMarket,
                    icon: <TenantInMarketIcon color={areTenantInMarketFiltersApplied ? withFiltersAppliedColor : color} />,
                },
                {
                    title: "Favorites", path: "/favorites", type: ListType.Favorites,
                    icon: <FavoriteIcon />,
                    hideInMobile: true,
                },
                {
                    title: "Notifications", path: "/notifications", type: ListType.Notifications,
                    icon: <NotificationIcon />,
                    hideInMobile: true,
                },
            ].map(item => (
                <NavbarItem key={item.path} title={item.title} path={item.path} type={item.type} hideInMobile={item.hideInMobile ?? false}>
                    {item.icon}
                </NavbarItem>
            ))}
        </nav>
    );
};

export default observer(Navbar);

const NavbarItem = observer(({ title, path, type, hideInMobile, children }: INavbarItemProps) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const listStore = useListStore();
    const viewSettingsStore = useViewSettingsStore();

    if (viewSettingsStore.isMobile && hideInMobile)
        return null;

    return (
        <div
            className={[
                styles.link,
                listStore.type === type || (viewSettingsStore.isMobile && pathname.startsWith(path)) ? styles.active : null,
            ].join(" ")}
            data-title={title}
            onClick={() => {
                if (viewSettingsStore.isMobile)
                    navigate(pathname !== path ? path : "/");
                else
                    listStore.toggleType(type);
            }}
        >
            {children}
        </div>
    );
});

interface INavbarItemProps {
    title: string;
    path: string;
    type: ListType;
    hideInMobile: boolean;
    children: React.ReactNode;
}